import { Suspense, useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";

import { useEntitlements } from "../hooks/useEntitlements.ts";
import { useAppearance } from "../shared/appearance-context.tsx";
import { useAuth } from "../shared/auth-context.tsx";
import { HelpProvider, useHelp } from "../shared/help-context.tsx";
import { filterNavGroups, visibleNavPaths } from "../shared/navServices.mjs";
import { WhiteLabelChromeProvider, useWhiteLabelChrome } from "../shared/white-label-chrome.tsx";
import { CommandPalette } from "./CommandPalette.tsx";
import { HelpDrawer } from "./HelpDrawer.tsx";
import { HelpLauncher } from "./HelpLauncher.tsx";
import { Icon, type IconName } from "./Icon.tsx";
import { OperatorBrand } from "./OperatorBrand.tsx";
import { RouteErrorBoundary } from "./RouteErrorBoundary.tsx";
import { ThemeToggle } from "./ThemeToggle.tsx";

/**
 * Shown while a lazy route chunk loads. Sized to the content region rather than
 * the viewport: the shell's own chrome is still on screen around it, so a
 * `100vh` panel here would overflow the layout and push the page.
 */
function RouteLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      data-route-loading="true"
      style={{
        display: "grid",
        placeItems: "center",
        padding: "48px 24px",
        color: "var(--ink-soft)",
        fontFamily: "var(--mono)",
        letterSpacing: "0.08em",
      }}
    >
      <span><span className="dot" /> Loading…</span>
    </div>
  );
}

/**
 * Authenticated app shell — left sidebar nav + main outlet for nested routes.
 * The shell stays mounted across route transitions inside the protected
 * area, so the chat-card assistant preview state can survive a navigation
 * between Playground and Settings.
 */

const GROUPS: {
  label: string;
  icon: IconName;
  items: { to: string; text: string }[];
}[] = [
  { label: "Overview", icon: "eye", items: [{ to: "/overview", text: "Home" }] },
  {
    label: "Conversations",
    icon: "email",
    items: [
      { to: "/inbox", text: "Inbox" },
      { to: "/calls", text: "Calls" },
      { to: "/analytics", text: "Analytics" },
      { to: "/quality", text: "Quality" },
    ],
  },
  {
    label: "Configure",
    icon: "spark",
    items: [
      { to: "/aria", text: "AI Studio" },
      { to: "/assistant", text: "Assistant" },
      { to: "/appearance", text: "Appearance" },
      { to: "/engagement", text: "Proactive" },
      { to: "/receptionist", text: "Receptionist" },
      { to: "/settings#knowledge", text: "Knowledge" },
      { to: "/settings#business-profile", text: "Business profile" },
      { to: "/knowledge-improvement", text: "Knowledge queue" },
      { to: "/playground", text: "Playground" },
      { to: "/evaluation", text: "Evaluation" },
    ],
  },
  {
    label: "Setup",
    icon: "check",
    items: [
      { to: "/install", text: "Install" },
      { to: "/settings#account", text: "Settings" },
    ],
  },
];

/**
 * Two sidebar entries share `/settings` and are only told apart by their hash,
 * so a destination that carries a hash must match on pathname *and* hash —
 * otherwise both light up at once, and both keep lighting at every other
 * Settings hash.
 *
 * A hash-less destination still owns its sub-routes: `/analytics/print` is
 * rendered inside this shell, and an operator standing there should see
 * Analytics marked current rather than a sidebar with nothing highlighted.
 * No destination carries a query string, so none is parsed.
 */
function isCurrentDestination(
  destination: string,
  location: { pathname: string; hash: string },
): boolean {
  const [pathname, ...hashParts] = destination.split("#");
  const hash = hashParts.length ? `#${hashParts.join("#")}` : "";
  if (hash) return location.pathname === pathname && location.hash === hash;
  return location.pathname === pathname
    || location.pathname.startsWith(`${pathname}/`);
}

/**
 * Collapsed-rail state, persisted the way the design handoff persisted it
 * (`operator-app.js`): one localStorage key, read once at mount so the very
 * first paint is already in the right shape, and re-written on every flip.
 *
 * The default when nothing is stored is "collapsed under 900px" — a viewport
 * that narrow cannot afford 240px of chrome, but is still too wide to be
 * showing the burger drawer.
 */
const RAIL_KEY = "cspro.nav.v1";

function storedRail(): boolean {
  try {
    const stored = localStorage.getItem(RAIL_KEY);
    if (stored) return stored === "rail";
  } catch {
    /* storage may be blocked; fall through to the viewport default */
  }
  return typeof window !== "undefined" && window.innerWidth <= 900;
}

function persistRail(rail: boolean): void {
  try {
    localStorage.setItem(RAIL_KEY, rail ? "rail" : "open");
  } catch {
    /* storage may be blocked; the in-memory state still works */
  }
}

/**
 * Satatam Help wraps the whole shell rather than sitting beside the launcher,
 * because the thread, the `?` shortcut and `openWith` all have to be reachable
 * from inside the routed page — the Overview setup steps open Help with a
 * question already asked. The React key is the tenant id and nothing else: a
 * route change never restarts a conversation, while an account switch (and
 * the first render after a session resolves, when the id is still "") remounts
 * the provider, so one tenant's thread can never be written under another's
 * storage key.
 */
export function AppShell() {
  const { session } = useAuth();
  return (
    <HelpProvider key={session?.tenantId || ""} tenantId={session?.tenantId || ""}>
      <AppShellInner />
    </HelpProvider>
  );
}

function AppShellInner() {
  const { session, signOut } = useAuth();
  const { operatorBranding } = useAppearance();
  const help = useHelp();
  const chrome = useWhiteLabelChrome();
  const location = useLocation();
  const { services } = useEntitlements();
  /**
   * `services === null` covers BOTH "not yet loaded" and "plan unreadable" --
   * `useEntitlements` starts and stays there until a readable answer lands,
   * so this never flash-hides an item the operator already has. `visible ===
   * null` (unknown, undecided trial, or both services bought) shows every
   * gated item too; otherwise only the paths `visibleNavPaths` names survive
   * the filter, and a group left with none is dropped rather than rendered
   * with an empty label.
   */
  const visible = visibleNavPaths(services);
  const visibleGroups = filterNavGroups(GROUPS, visible);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [railed, setRailed] = useState(storedRail);
  const close = () => setDrawerOpen(false);

  /**
   * The rail is a `body` class, not a prop, because the pieces it restyles are
   * spread across the sidebar, the toggle (which is `position:fixed`, so it
   * cannot inherit from an ancestor) and `.app-main`'s padding. Suppress the
   * width transition on the first pass so a session that starts collapsed does
   * not animate open-to-rail in front of the operator.
   */
  useEffect(() => {
    document.body.classList.toggle("nav-rail", railed);
  }, [railed]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("nav-no-anim");
    const frame = requestAnimationFrame(() => root.classList.remove("nav-no-anim"));
    return () => {
      cancelAnimationFrame(frame);
      root.classList.remove("nav-no-anim");
      document.body.classList.remove("nav-rail");
    };
  }, []);

  const toggleRail = useCallback(() => {
    setRailed((v) => {
      persistRail(!v);
      return !v;
    });
  }, []);

  /**
   * Navigating only closes the burger drawer. It deliberately does NOT persist
   * the rail: the handoff wrote "collapsed" on every nav click while leaving
   * the current view expanded, so the stored preference and the thing on
   * screen disagreed until the next load. From the operator's seat the toggle
   * looked broken -- expand the sidebar, click anywhere, come back, and it is
   * collapsed again with no way to make the choice stick. The explicit toggle
   * (and ⌘\) are the only things allowed to write this preference.
   */
  const onNavigate = useCallback(() => {
    close();
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // Global ⌘K / Ctrl-K opens the command palette. ⌘K has no default action in
  // this app's inputs, so we intercept globally and preventDefault. Don't open
  // it on top of another modal/confirm dialog if one is already up.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        const otherModalUp =
          !paletteOpen && !!document.querySelector('[role="dialog"][aria-modal="true"]');
        if (otherModalUp) return;
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen]);

  // ⌘\ / Ctrl-\ collapses and expands the rail. No browser or input claims it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        toggleRail();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleRail]);

  /* Published once, read everywhere. The hook has side effects -- accent
     tokens, tab title, favicon -- so pages that only need to name the product
     read the value rather than calling it again. */
  return (
    <WhiteLabelChromeProvider value={chrome}>
    <div className={`app-shell${drawerOpen ? " drawer-open" : ""}`}>
      <div className="app-topbar">
        <button
          type="button"
          className="app-burger"
          aria-label="Open menu"
          onClick={() => setDrawerOpen((v) => !v)}
        >
          <Icon name="hamburger" />
        </button>
        <NavLink to="/overview" className="app-brand" onClick={close}>
          <OperatorBrand branding={operatorBranding} chrome={chrome} />
        </NavLink>
      </div>
      {drawerOpen && (
        <div className="app-backdrop" onClick={close} aria-hidden="true" />
      )}
      <nav className="app-sidebar" aria-label="Primary">
        <NavLink
          to="/overview"
          className="app-brand"
          aria-label={`${chrome.productLabel} — Operator home`}
          onClick={close}
        >
          <OperatorBrand branding={operatorBranding} chrome={chrome} />
        </NavLink>
        <div className="app-nav-groups">
          {visibleGroups.map((g) => (
            <div
              className={
                `app-nav-group${g.items.some((it) => isCurrentDestination(it.to, location))
                  ? " is-active"
                  : ""}`
              }
              key={g.label}
            >
              <div className="app-eyebrow nav-group-label">
                <span className="nav-group-icon" aria-hidden="true">
                  <Icon name={g.icon} />
                </span>
                <span>{g.label}</span>
              </div>
              {g.items.map((it) => {
                const current = isCurrentDestination(it.to, location);
                return (
                  <Link
                    key={it.text}
                    to={it.to}
                    onClick={onNavigate}
                    aria-current={current ? "page" : undefined}
                    className={current ? "nav-link is-active" : "nav-link"}
                  >
                    {it.text}
                  </Link>
                );
              })}
              {/*
                Help is a button, not a destination — it opens the drawer over
                whatever the operator is already looking at, so the answer can
                name the control in front of them. It wears `.nav-link` for the
                same reason sign-out does: the rail's 58px collapse styles that
                class, and a bespoke one would drop out of the rail.
              */}
              {g.label === "Setup" && (
                <button
                  type="button"
                  className="nav-link"
                  data-help-nav
                  onClick={() => {
                    onNavigate();
                    help.openWith();
                  }}
                >
                  Help
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="app-sidebar-footer">
          {/* Attribution restored when white-labelling removed the product
              name. `.powered-by` carries `order:-1`, so it sits above the
              footer controls without being first in the tab order. */}
          {chrome.showPoweredBy && (
            <span className="powered-by">Powered by Satatam</span>
          )}
          <button
            type="button"
            className="cmdk-affordance"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
            title="Command palette"
          >
            <span>Search & commands</span>
            <kbd className="cmdk-kbd">⌘K</kbd>
          </button>
          <ThemeToggle variant="nav" />
          {session && (
            <button
              type="button"
              onClick={signOut}
              className="nav-link"
              aria-label="Sign out"
            >
              {/*
                The chip splits the operator's name from the sign-out mark so
                the rail can drop the name and keep a 38px square: see
                `body.nav-rail .user-chip .un` in redesign-premium.css.
              */}
              <span className="user-chip">
                <span className="ua">{(session.email[0] || "?").toUpperCase()}</span>
                <span className="un">{session.email.split("@")[0]}</span>
              </span>
              <Icon name="signout" />
            </button>
          )}
        </div>
        {/*
          Pinned to the sidebar's top-right corner. It is `position:fixed`
          rather than absolutely placed inside the nav so it stays put while
          the sidebar scrolls, which is what makes it findable in the 58px rail
          where nothing else is.
        */}
        <button
          type="button"
          className="nav-toggle"
          id="nav-toggle"
          onClick={toggleRail}
          aria-expanded={!railed}
          aria-label={railed ? "Expand sidebar" : "Collapse sidebar"}
          title={(railed ? "Expand" : "Collapse") + " sidebar  ⌘\\"}
        >
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2.7" y="3.5" width="14.6" height="13" rx="3.2" />
            <path d="M7.7 3.5v13" />
            <path d="M12.6 8.4 11 10l1.6 1.6" />
          </svg>
        </button>
      </nav>
      <main className="app-main">
        {/*
          Suspense and the error boundary wrap the outlet, not the shell. Route
          pages are lazy chunks, so both a pending fetch and a failed one are
          content-region states — scoping them here keeps the sidebar and topbar
          painted and interactive throughout, which is what makes the shell
          "stay mounted across route transitions" as documented above. Wrapping
          the shell instead would unmount the nav on every first visit to a
          route and replace it with a full-screen panel.
        */}
        <RouteErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <HelpLauncher />
      <HelpDrawer />
    </div>
    </WhiteLabelChromeProvider>
  );
}
